'use client';

import dynamic from 'next/dynamic';
import * as React from 'react';
import { useEffect, useRef, useState, useContext, forwardRef, useImperativeHandle, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import ImageNotFound from './imageNotFound';
import { Tooltip } from "@heroui/tooltip";
import { MediaObject, MediaType, SourceInfo } from '@/shared/types';
import MediaPlayer from './media/player';
import { FileAudioIcon, FileTextIcon, FileHtmlIcon, ArrowClockwiseIcon, ArrowCounterClockwiseIcon, ArrowsOutSimpleIcon, ArrowsInSimpleIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, XCircleIcon, TextTIcon, TableIcon, CropIcon, FrameCornersIcon } from "@phosphor-icons/react";
import { getCurrentSourceInfo, useWindowSize } from '@/shared/clientFunctions';
import { checkSourceContentType, extractPathAfterBulk, formatTimeIndex, getMediaObjectUrlKey, getMediaType, isMetaBundleFilename, isMetaFile, parseCitationPageFromMedia } from '@/shared/functions';
import { FileTypes } from '@/shared/constants';
import MetaFileDisplay from './metaPreviewViewer';
import { FeaturesContext, FullScreenContext } from '@/app/contexts';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/modal';
import { Button } from '@heroui/button';
import html2canvas from 'html2canvas';
import { useOptionalToolsSharedCaseData, stripWebpToRawBase64, MAX_CAPTURE_BASE64_CHARS, MAX_CAPTURES } from '@/components/tools-shared-case-data';
import { useToolsDrawerCaptureApplyNonce } from '@/components/tools-drawer';
const CAPTURE_TOOLTIP_CLASSNAMES = {
  base: 'before:bg-tooltip-bg z-[99999999]',
  content: 'flex flex-row items-center justify-center w-[200px] wrap',
  arrow: 'text-white'
} as const;
const CAPTURE_MODAL_CLASSNAMES = {
  wrapper: 'captureModalWrapper z-[99999999] my-0',
  base: 'captureModalBase z-[99999999] my-0 mx-0 m-0 sm:my-0 sm:mx-0 sm:m-0 bg-gray-900',
  body: 'captureModalBody z-[99999999]',
  footer: 'captureModalFooter z-[99999999]',
  header: 'captureModalHeader z-[99999999]',
  closeButton: 'captureModalCloseButton z-[99999999]',
  backdrop: 'captureModalBackdrop z-[99999999]'
} as const;
const XlsxViewer = dynamic(() => import('@/components/xlsx-viewer/XlsxViewer'), {
  ssr: false
});
const PdfViewer = dynamic(() => import('./pdfViewer'), {
  ssr: false
});
const PlainTextViewer = dynamic(() => import('@/components/plain-text-viewer/PlainTextViewer'), {
  ssr: false
});
const MarkdownViewer = dynamic(() => import('@/components/markdown-viewer/MarkdownViewer'), {
  ssr: false
});
const isMbox = (url: string | undefined) => {
  return url?.endsWith('.mbox') || url?.endsWith('.mbx');
};

/**
 * For mbox items, props often keep the container .mbox URL while resolved state uses a per-email .eml URL,
 * so getMediaObjectUrlKey(parent) !== getMediaObjectUrlKey(resolved). Use the same identity as the mbox fetch
 * effect (currentEmailId) so updates that only touch mediaContext (e.g. capture apply) do not reset the viewer.
 */
function getMboxEmailIdentityKey(media: MediaObject | undefined): string {
  if (!media?.originalUrl || !isMbox(media.originalUrl)) return '';
  const previewUrl = media.mediaContext?.previewUrl || '';
  let currentEmailId = previewUrl;
  if (previewUrl) {
    const parsedEmailIndex = previewUrl?.split('/').pop()?.split('.')[0]?.split('_')[1];
    if (!!parsedEmailIndex && !Number.isNaN(parseInt(parsedEmailIndex))) {
      currentEmailId = `${media.originalUrl || ''}_${parsedEmailIndex}`;
    } else {
      currentEmailId = previewUrl;
    }
  } else {
    currentEmailId = `${media.originalUrl || ''}_0`;
  }
  return currentEmailId;
}
function getMediaDisplayIdentityKey(media: MediaObject | undefined): string {
  if (!media) return '';
  if (isMbox(media.originalUrl)) {
    return getMboxEmailIdentityKey(media);
  }
  return getMediaObjectUrlKey(media);
}
const isXmlFile = (item: MediaObject | undefined) => {
  if (!item) return false;
  const url = item.originalUrl || item.srcToUse || item.mediaUrl || item.mediaSrc || '';
  const extension = (url.split(/[#?]/)[0].split('.').pop() || '').trim().toLowerCase();
  return extension === 'xml';
};
const isPlainTextFile = (item: MediaObject | undefined) => {
  if (!item || item.mediaType !== 'text' || !item.srcToUse) return false;
  const url = item.originalUrl || item.srcToUse || '';
  return !isMbox(url) && !isMetaFile(item) && !isXmlFile(item);
};
const isMarkdownPreview = (item: MediaObject | undefined) => {
  if (!item) return false;
  const url = item.srcToUse || item.previewFilePath || item.mediaUrl || item.mediaSrc || item.originalUrl || '';
  const pathOnly = url.split(/[?#]/)[0].toLowerCase();
  return pathOnly.endsWith('.md') || pathOnly.endsWith('.markdown');
};
const getSrcToUse = (srcItem: MediaObject) => {
  let newSrcToUse = undefined;
  if (srcItem.sourceInfo?.previewObjectUrl && srcItem.sourceInfo?.previewObjectUrl !== '') {
    newSrcToUse = srcItem.sourceInfo?.previewObjectUrl;
  } else if (srcItem.previewFilePath && srcItem.previewFilePath !== '') {
    newSrcToUse = srcItem.previewFilePath;
  } else if (srcItem.sourceFilePath && srcItem.sourceFilePath !== '') {
    newSrcToUse = srcItem.sourceFilePath;
  } else {
    newSrcToUse = srcItem.originalUrl || srcItem.mediaUrl || srcItem.mediaSrc || undefined;
  }
  return newSrcToUse;
};

/** Meta bundle row (`isMetaFile`) may preview a non-bundle URL; align `mediaType` with that URL and `sourceInfo`. */
function mediaObjectForMetaPreviewMismatch(item: MediaObject): MediaObject {
  const src = item.srcToUse?.trim();
  if (!src || !isMetaFile(item) || isMetaBundleFilename(src)) {
    return item;
  }
  const previewSourceInfo = {
    ...item.sourceInfo,
    contentCategories: [],
    sourceContentType: src,
    sourceObjectUrl: src,
    mediaUrl: src
  } as SourceInfo;
  const previewMediaObject = {
    ...item,
    previewFilePath: '',
    mediaUrl: src,
    mediaSrc: src,
    sourceUrl: src,
    mediaType: 'unknown',
    sourceInfo: previewSourceInfo
  } as MediaObject;
  const derivedType = getMediaType(previewMediaObject, previewSourceInfo);
  if (derivedType === item.mediaType) {
    return item;
  }
  return {
    ...previewMediaObject,
    mediaType: derivedType
  };
}
export const fetchMboxData = async (sourceFilePath: string | undefined, emailUrl: string | undefined, signal?: AbortSignal) => {
  try {
    if (!sourceFilePath) {
      return null;
    }
    let emailIndex = 0;
    if (emailUrl) {
      const parsedEmailIndex = emailUrl?.split('/').pop()?.split('.')[0]?.split('_')[1];
      if (!!parsedEmailIndex && !Number.isNaN(parseInt(parsedEmailIndex))) {
        emailIndex = parseInt(parsedEmailIndex);
      }
    }
    const response = await fetch(`/api/protected/junkdrawer/mbox?url=${encodeURIComponent(sourceFilePath)}&emailIndex=${emailIndex}`, {
      signal
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    // If aborted, return null without logging
    if (error instanceof Error && error.name === 'AbortError') {
      return null;
    }
    return null;
  }
};
const rotatableMediaTypes = ['image', 'pdf', 'video'];
const zoomableMediaTypes = ['image', 'pdf'];
export type CaptureRegion = {
  x: number | undefined;
  y: number | undefined;
  width: number | undefined;
  height: number | undefined;
};
export type CapturedDataObject = {
  captureData: string;
  s3Key: string;
};
export const maxDuration = 300; // allow the long requests for huge files
export type MediaDisplayProps = {
  // May be undefined briefly for example with unified sources while adding a timeline event source
  // need to investigate if there's a better way to make sure we always have this before accessing it
  mediaItem: MediaObject | undefined;
  containerHeight?: number;
  containerWidth?: number;
  isPresentation?: boolean;
  isAccordion?: boolean;
  isTable?: boolean;
  isEventModal?: boolean;
  showTranscript?: boolean;
  showTranscriptMarkers?: boolean;
  transcriptPosition?: 'below' | 'side';
  updateMediaContext?: (field: string | object, value?: any) => void;
  isTitleSlide?: boolean;
  isUnifiedSources?: boolean;
  isPreview?: boolean;
  isLinkedMedia?: boolean;
  sourcesExpanded?: boolean;
  isMediaShareDrawer?: boolean;
  showToolbarExpandButton?: boolean;
  isSourceCard?: boolean;
  /** Included on /meta-viewer links from MetaFileDisplay when set. */
  caseId?: string | null;
  /** When true, MetaFileDisplay does not render the SMV button (e.g. source card renders it above citation). */
  hideMetaSocialViewerButton?: boolean;
};
export interface MediaDisplayRef {
  toggleFullScreen: () => void;
}
const MediaDisplayComponent = forwardRef<MediaDisplayRef, MediaDisplayProps>(({
  mediaItem,
  containerHeight,
  containerWidth,
  isPresentation,
  isAccordion,
  isTable,
  isEventModal,
  showTranscript,
  showTranscriptMarkers,
  transcriptPosition = 'below',
  updateMediaContext,
  isTitleSlide,
  isUnifiedSources,
  isPreview,
  isLinkedMedia,
  sourcesExpanded,
  isMediaShareDrawer,
  showToolbarExpandButton = true,
  isSourceCard = false,
  caseId = null,
  hideMetaSocialViewerButton = false
}: MediaDisplayProps, ref) => {
  const textRef = useRef<HTMLDivElement>(null);
  const mediaDisplayRef = useRef<HTMLDivElement>(null);
  const [temporaryRotation, setTemporaryRotation] = useState<number | undefined>(undefined);
  const [imageErrors, setImageErrors] = useState<Record<string, {
    fileNotFound: boolean;
  }>>({});
  const [htmlErrors, setHtmlErrors] = useState<Record<string, {
    fileNotFound: boolean;
  }>>({});
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const [localContainerWidth, setLocalContainerWidth] = useState<number | undefined>(containerWidth);
  const [localContainerHeight, setLocalContainerHeight] = useState<number | undefined>(containerHeight);
  const [normalContainerDimensions, setNormalContainerDimensions] = useState<{
    width: number;
    height: number;
  } | undefined>(undefined);
  const [itemToUse, setItemToUse] = useState<MediaObject | undefined>(undefined);
  const [viewAsCsvLike, setViewAsCsvLike] = useState<boolean>(false);
  const [canToggleCsv, setCanToggleCsv] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [normalContainerWidth, setNormalContainerWidth] = useState<number | undefined>(undefined);
  const [normalContainerHeight, setNormalContainerHeight] = useState<number | undefined>(undefined);
  const [imageDimensions, setImageDimensions] = useState<{
    height: number;
    width: number;
  } | undefined>(undefined);
  /** Tracks which mbox email was fetched; compare with getMediaDisplayIdentityKey so .mbox props vs resolved .eml URLs do not fight. */
  const mboxFetchedEmailIdRef = useRef<string | null>(null);
  const currentMboxFetchRef = useRef<{
    emailId: string;
    abortController: AbortController;
  } | null>(null);
  const currentPdfPageRef = useRef<number | null>(null);
  const lastMediaItemKeyForViewCaptureRef = useRef<string | null>(null);
  const lastMediaItemKeyRef = useRef<string>('');
  const {
    width: windowWidth,
    height: windowHeight
  } = useWindowSize();
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const captureFeatureEnabled = Boolean(featureFlags?.capture);
  const {
    isFullscreen,
    setIsFullscreen,
    fullscreenMediaItem,
    setFullscreenMediaItem,
    linkedMediaItem,
    setLinkedMediaItem
  } = useContext(FullScreenContext);
  const sharedCaseData = useOptionalToolsSharedCaseData();
  const captureApplyNonce = useToolsDrawerCaptureApplyNonce();
  const sharedCaseDataRef = useRef(sharedCaseData);
  sharedCaseDataRef.current = sharedCaseData;
  const itemToUseRef = useRef(itemToUse);
  itemToUseRef.current = itemToUse;
  const metaViewerCaseId = useMemo(() => {
    const fromProp = caseId != null && String(caseId).trim() !== "" ? String(caseId).trim() : undefined;
    const fromShared = sharedCaseData?.caseId != null && String(sharedCaseData.caseId).trim() !== "" ? String(sharedCaseData.caseId).trim() : undefined;
    return fromProp ?? fromShared ?? null;
  }, [caseId, sharedCaseData?.caseId]);
  const prevCaptureApplyNonceRef = useRef<number | null>(null);
  const captureRef = useRef<CaptureRegion | undefined>(undefined);
  const captureOverlayRef = useRef<CaptureRegion | undefined>(undefined);
  const capModalRef = useRef<HTMLDivElement>(null);
  const [captureMode, setCaptureMode] = useState<boolean>(false);
  const [captureRegion, setCaptureRegion] = useState<CaptureRegion | undefined>(undefined);
  const [captureRegionOverlay, setCaptureRegionOverlay] = useState<CaptureRegion | undefined>(undefined);
  const [capturedDataAvailable, setCapturedDataAvailable] = useState<CapturedDataObject | undefined>(undefined);
  const [applyingCapturedData, setApplyingCapturedData] = useState<boolean>(false);
  const [removingCapturedData, setRemovingCapturedData] = useState<boolean>(false);
  const [viewCapture, setViewCapture] = useState<boolean>(false);
  const [pdfPageReady, setPdfPageReady] = useState<boolean>(false);
  useEffect(() => {
    if (!isEventModal) {
      prevCaptureApplyNonceRef.current = null;
      return;
    }
    if (prevCaptureApplyNonceRef.current === null) {
      prevCaptureApplyNonceRef.current = captureApplyNonce;
      return;
    }
    if (captureApplyNonce !== prevCaptureApplyNonceRef.current) {
      prevCaptureApplyNonceRef.current = captureApplyNonce;
      setViewCapture(true);
    }
  }, [isEventModal, captureApplyNonce]);
  useEffect(() => {
    if (!mediaItem) return;
    setItemToUse(prev => prev ? {
      ...prev,
      mediaContext: {
        ...prev.mediaContext,
        capture: mediaItem?.mediaContext?.capture
      }
    } as MediaObject : prev);
  }, [mediaItem?.mediaContext?.capture]);
  const handleUpdateCapture = useCallback((captureData: string | undefined) => {
    const latestItem = itemToUseRef.current;
    const captureDataObject = {
      captureData: captureData || '',
      s3Key: latestItem?.originalUrl || latestItem?.mediaUrl || latestItem?.mediaSrc || ''
    };
    localStorage.setItem('captureData', JSON.stringify(captureDataObject));
    setCapturedDataAvailable(captureDataObject);
    const remote = sharedCaseDataRef.current;
    if (!captureData || !remote?.hydrated) return;
    const raw = stripWebpToRawBase64(captureData);
    if (!raw || raw.length > MAX_CAPTURE_BASE64_CHARS) return;
    remote.setCaptures(prev => {
      const entry = {
        id: crypto.randomUUID(),
        webpBase64: raw,
        createdAt: Date.now()
      };
      return [entry, ...prev].slice(0, MAX_CAPTURES);
    });
  }, []);
  const handleCaptureRegion = useCallback((cap: CaptureRegion | undefined) => {
    const captureCanvas = document.getElementById('imageCaptureCanvas') as HTMLCanvasElement;
    if (!!captureCanvas && !!cap && !!cap.width && !!cap.height && !!cap.x && !!cap.y) {
      const captureData = captureCanvas.toDataURL('image/webp');
      handleUpdateCapture(captureData);
      setCaptureRegion(undefined);
    }
  }, [handleUpdateCapture]);
  useEffect(() => {
    if (captureRegionOverlay || !captureRegion || !captureRegion.width || !captureRegion.height || !captureRegion.x || !captureRegion.y) return;
    const cap = captureRef.current;
    captureRef.current = undefined;
    if (cap?.x && cap?.y && cap?.width && cap?.height && capModalRef.current) {
      captureRef.current = {
        x: undefined,
        y: undefined,
        width: undefined,
        height: undefined
      };
      html2canvas(mediaDisplayRef.current as HTMLElement, {
        x: cap.x,
        y: cap.y,
        width: cap.width,
        height: cap.height
      }).then(canvas => {
        canvas.id = "imageCaptureCanvas";
        if (capModalRef.current) {
          capModalRef.current.appendChild(canvas);
        } else {
          // failure, let's reset
          captureOverlayRef.current = undefined;
          captureRef.current = undefined;
          setCaptureRegionOverlay(undefined);
          setCaptureRegion(undefined);
          setCaptureMode(false);
        }
      });
    }
  }, [captureRegion, captureRegionOverlay]);
  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!captureMode) return;
    e.preventDefault();
    e.stopPropagation();
    const {
      clientX,
      clientY
    } = e;
    const {
      left,
      top
    } = e.currentTarget.getBoundingClientRect();
    const relX = clientX - left;
    const relY = clientY - top;
    captureRef.current = {
      x: relX,
      y: relY,
      width: 0,
      height: 0
    };
    captureOverlayRef.current = {
      x: relX,
      y: relY,
      width: 0,
      height: 0
    };
  }, [captureMode]);
  const onMouseUp = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!captureMode) return;
    const cap = captureRef.current;
    if (cap?.x !== undefined && cap?.y !== undefined) {
      e.preventDefault();
      e.stopPropagation();
      const {
        clientX,
        clientY
      } = e;
      const {
        left,
        top
      } = e.currentTarget.getBoundingClientRect();
      const relX = clientX - left;
      const relY = clientY - top;
      captureRef.current = {
        x: cap.x,
        y: cap.y,
        width: relX - cap.x,
        height: relY - cap.y + 3
      };
      const capOverlay = captureOverlayRef.current;
      if (capOverlay && capOverlay.x !== undefined && capOverlay.y !== undefined && capOverlay.width !== undefined && capOverlay.height !== undefined) {
        captureOverlayRef.current = {
          x: capOverlay.x,
          y: capOverlay.y,
          width: relX - capOverlay.x,
          height: relY - capOverlay.y
        };
        const capOverlayNew = {
          x: capOverlay.x - 1,
          y: capOverlay.y - 1,
          width: (captureOverlayRef.current?.width || 0) + 3,
          height: (captureOverlayRef.current?.height || 0) + 6
        };
        setCaptureRegionOverlay({
          ...capOverlayNew
        });
      }
      setCaptureRegion({
        ...captureRef.current
      });
      setTimeout(() => {
        captureOverlayRef.current = undefined;
        setCaptureRegionOverlay(undefined);
      }, 100);
    }
  }, [captureMode]);
  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!captureMode) return;
    const capOverlay = captureOverlayRef.current;
    if (capOverlay && capOverlay.x !== undefined && capOverlay.y !== undefined && capOverlay.width !== undefined && capOverlay.height !== undefined) {
      e.preventDefault();
      e.stopPropagation();
      const {
        clientX,
        clientY
      } = e;
      const {
        left,
        top
      } = e.currentTarget.getBoundingClientRect();
      const relX = clientX - left;
      const relY = clientY - top;
      captureOverlayRef.current = {
        x: capOverlay.x,
        y: capOverlay.y,
        width: relX - capOverlay.x,
        height: relY - capOverlay.y
      };
      const capOverlayNew = {
        x: capOverlay.x - 1,
        y: capOverlay.y - 1,
        width: (captureOverlayRef.current.width || 0) + 3,
        height: (captureOverlayRef.current.height || 0) + 6
      };
      setCaptureRegionOverlay({
        ...capOverlayNew
      });
    }
  }, [captureMode]);
  const handlePdfPageChange = useCallback((page: number) => {
    currentPdfPageRef.current = page;
  }, []);
  const isGoingToRenderSetStillFramePrompt = useMemo(() => (isUnifiedSources || isTable) && !mediaItem?.mediaContext?.canvas?.stillFrameImage && !isAccordion, [isUnifiedSources, isTable, mediaItem?.mediaContext?.canvas?.stillFrameImage, isAccordion]);
  const toggleFullScreen = useCallback(() => {
    if (isLinkedMedia) {
      setLinkedMediaItem(null);
      return;
    }
    const newFullScreen = !isFullscreen;
    setZoomLevel(0);
    if (newFullScreen) {
      if (containerWidth && containerHeight) {
        setNormalContainerDimensions({
          width: containerWidth,
          height: containerHeight
        });
      } else if (mediaDisplayRef.current?.parentElement?.clientWidth && mediaDisplayRef.current?.parentElement?.clientHeight) {
        setNormalContainerDimensions({
          width: mediaDisplayRef.current.parentElement.clientWidth,
          height: mediaDisplayRef.current.parentElement.clientHeight
        });
      }
      const mediaItemCopy = Object.assign({}, itemToUseRef.current);
      if (currentPdfPageRef.current && mediaItemCopy.mediaContext) {
        mediaItemCopy.mediaContext = {
          ...mediaItemCopy.mediaContext,
          pageNumber: currentPdfPageRef.current
        };
      }
      setFullscreenMediaItem(mediaItemCopy);
    } else {
      setFullscreenMediaItem(mediaItem ? {
        ...mediaItem
      } : null);
    }
    setIsFullscreen(newFullScreen);
  }, [isLinkedMedia, isFullscreen, containerWidth, containerHeight, mediaItem, setLinkedMediaItem, setFullscreenMediaItem, setIsFullscreen]);
  useImperativeHandle(ref, () => ({
    toggleFullScreen
  }), [toggleFullScreen]);
  useEffect(() => {
    if (!containerWidth) return;
    setLocalContainerWidth(containerWidth);
  }, [containerWidth, imageLoaded]);
  useEffect(() => {
    if (!containerHeight) return;
    setLocalContainerHeight(containerHeight);
  }, [containerHeight]);
  const handleSetTemporaryRotation = useCallback((direction: 'cc' | 'c') => {
    let currentRotation;
    if (!!updateMediaContext && temporaryRotation === undefined) {
      currentRotation = mediaItem?.mediaContext?.rotation || 0;
    } else {
      currentRotation = temporaryRotation || 0;
    }
    if (direction === 'cc') {
      let newRotation = currentRotation === 0 ? 270 : (currentRotation || 0) - 90;
      if (newRotation < 0) {
        newRotation = 270;
      }
      setTemporaryRotation(newRotation);
    } else {
      let newRotation = currentRotation === 270 ? 0 : (currentRotation || 0) + 90;
      if (newRotation > 270) {
        newRotation = 0;
      }
      setTemporaryRotation(newRotation);
    }
  }, [updateMediaContext, temporaryRotation, mediaItem?.mediaContext?.rotation]);
  const zoomIn = useCallback(() => {
    setZoomLevel(z => Math.min(500, z + 2));
  }, []);
  const zoomOut = useCallback(() => {
    setZoomLevel(z => Math.max(-500, z - 2));
  }, []);
  const onImageLoaded = useCallback((event: {
    target: {
      naturalHeight: number;
      naturalWidth: number;
    };
  }) => {
    if (isFullscreen) {
      const imageDims = {
        height: event.target.naturalHeight,
        width: event.target.naturalWidth
      };
      setImageDimensions(imageDims);
      const container = document.getElementById('fullScreenMediaDisplayInner')?.querySelector('.mediaDisplayImageDiv');
      if (container) {
        let newZoomLevel = 0;
        let newScale = 1;
        if (imageDims.height > container.clientHeight) {
          newScale = container.clientHeight / imageDims.height;
          newZoomLevel = Math.max(-500, Math.floor((newScale - 1) / 0.02));
        }
        if (imageDims.width * newScale > container.clientWidth) {
          newScale = container.clientWidth / imageDims.width;
          newZoomLevel = Math.max(-500, Math.floor((newScale - 1) / 0.02));
        }
        setZoomLevel(prev => newZoomLevel !== prev ? newZoomLevel : prev);
      }
    }
    setImageLoaded(true);
  }, [isFullscreen]);
  const handleSetRotation = useCallback((direction: 'cc' | 'c') => {
    if (!!updateMediaContext) {
      const base = itemToUseRef.current ?? mediaItem;
      if (!base) return;
      const lastUpdated = Date.now();
      let newRotation;
      if (direction === 'cc') {
        newRotation = base.mediaContext?.rotation === 0 ? 270 : (base.mediaContext?.rotation || 0) - 90;
        if (newRotation < 0) {
          newRotation = 270;
        }
      } else {
        newRotation = base.mediaContext?.rotation === 270 ? 0 : (base.mediaContext?.rotation || 0) + 90;
        if (newRotation > 270) {
          newRotation = 0;
        }
      }
      const newMediaContext = {
        ...base.mediaContext,
        rotation: newRotation,
        lastUpdated
      };
      const newMediaItem = {
        ...base,
        mediaContext: newMediaContext
      };
      updateMediaContext(newMediaContext);
      setItemToUse(newMediaItem);
    } else {
      handleSetTemporaryRotation(direction);
    }
  }, [updateMediaContext, mediaItem, handleSetTemporaryRotation]);
  const handleImageErrors = useCallback((imgError: Record<string, {
    fileNotFound: boolean;
  }>, mediaItemIn: MediaObject) => {
    const srcToUseIn = getSrcToUse(mediaItemIn);
    if (srcToUseIn) {
      const imgErrorToUse = {
        ...imgError,
        fileNotFound: true
      };
      setImageErrors(prev => ({
        ...prev,
        [srcToUseIn]: imgErrorToUse
      }));
    }
  }, []);
  const handleUpdateMediaContext = useCallback((field: string | object, value?: any) => {
    if (!!updateMediaContext) {
      updateMediaContext(field, value);
    }
  }, [updateMediaContext]);
  useEffect(() => {
    if (!itemToUse) return;
    const fetchAndParseText = async () => {
      if (itemToUse?.mediaType !== 'text' || !itemToUse.srcToUse) return;
      if (isMbox(itemToUse.srcToUse)) return;
      if (isMarkdownPreview(itemToUse)) return;
      if (isPlainTextFile(itemToUse)) return;
      setCanToggleCsv(false);
      if (textRef.current) textRef.current.innerText = '';
      try {
        const src = `/api/proxy/${encodeURIComponent(itemToUse.srcToUse)}`;
        const response = await fetch(src);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const text = await response.text();
        if (textRef.current) textRef.current.innerText = text;
        const lines = text.split('\n').filter(l => l.trim()).slice(0, 20);
        if (lines.length >= 3) {
          for (const delim of [',', '\t', '|', ';']) {
            const counts = lines.map(l => l.split(delim).length);
            if (counts[0] >= 2 && counts.every(c => c === counts[0])) {
              setCanToggleCsv(true);
              break;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching text:', error);
        if (textRef.current) textRef.current.innerText = 'Error loading content';
      }
    };
    if (!viewCapture) {
      fetchAndParseText();
    }
  }, [isFullscreen, itemToUse, itemToUse?.srcToUse, viewAsCsvLike, viewCapture]);
  useEffect(() => {
    if (!mediaItem) {
      return;
    }
    const key = getMediaDisplayIdentityKey(mediaItem);
    if (key !== lastMediaItemKeyRef.current) {
      lastMediaItemKeyRef.current = key;
      setItemToUse(undefined); // Avoid showing stale content until checkSourceInfo runs
      setPdfPageReady(false);
      currentPdfPageRef.current = parseCitationPageFromMedia(mediaItem);
      handlePdfPageChange(currentPdfPageRef.current ?? 1);
    }
    if (captureFeatureEnabled) {
      const capturedDataStorageObject = localStorage.getItem('captureData');
      const capturedDataObject = capturedDataStorageObject ? JSON.parse(capturedDataStorageObject) as CapturedDataObject : undefined;
      setCapturedDataAvailable(capturedDataObject || undefined);
    }
    if (isMbox(mediaItem?.originalUrl)) {
      // Get the current email identifier from previewUrl
      // Extract email index from previewUrl if available, otherwise use the previewUrl itself
      const previewUrl = mediaItem?.mediaContext?.previewUrl || '';
      let currentEmailId = previewUrl;
      if (previewUrl) {
        // Try to extract email index for more reliable comparison
        const parsedEmailIndex = previewUrl?.split('/').pop()?.split('.')[0]?.split('_')[1];
        if (!!parsedEmailIndex && !Number.isNaN(parseInt(parsedEmailIndex))) {
          currentEmailId = `${mediaItem?.originalUrl || ''}_${parsedEmailIndex}`;
        } else {
          // Use previewUrl as identifier if we can't extract index
          currentEmailId = previewUrl;
        }
      } else {
        // If no previewUrl, use originalUrl with index 0 as identifier
        currentEmailId = `${mediaItem?.originalUrl || ''}_0`;
      }

      // Only fetch if this is a different email than what we've already fetched
      if (currentEmailId !== mboxFetchedEmailIdRef.current) {
        // Cancel any previous mbox fetch to prevent stale data from being set
        if (currentMboxFetchRef.current) {
          currentMboxFetchRef.current.abortController.abort();
        }

        // Create a new AbortController for this fetch
        const abortController = new AbortController();
        currentMboxFetchRef.current = {
          emailId: currentEmailId,
          abortController
        };
        const fetchMbox = async () => {
          // Capture the emailId at the start of the fetch to verify it's still current
          const fetchEmailId = currentEmailId;
          try {
            const mboxData = await fetchMboxData(mediaItem?.originalUrl || '', mediaItem?.mediaContext?.previewUrl || undefined, abortController.signal);

            // Only update state if this fetch is still for the current email
            // This prevents stale data from a previous fetch overwriting current data
            if (currentMboxFetchRef.current?.emailId !== fetchEmailId) {
              return;
            }
            if (mboxData) {
              // Track which email we fetched using the same identifier format
              const fetchedEmailId = mboxData.emailUrl ? (() => {
                const parsedEmailIndex = mboxData.emailUrl?.split('/').pop()?.split('.')[0]?.split('_')[1];
                if (!!parsedEmailIndex && !Number.isNaN(parseInt(parsedEmailIndex))) {
                  return `${mediaItem?.originalUrl || ''}_${parsedEmailIndex}`;
                }
                return mboxData.emailUrl;
              })() : currentEmailId;

              // Double-check we're still fetching the current email before updating state
              if (currentMboxFetchRef.current?.emailId === fetchEmailId) {
                mboxFetchedEmailIdRef.current = fetchedEmailId;
                const newMediaItem = {
                  ...mediaItem,
                  mediaType: 'text',
                  mediaUrl: mboxData.emailUrl,
                  previewFilePath: mboxData.emailUrl,
                  mediaContext: {
                    ...mediaItem?.mediaContext,
                    previewUrl: mboxData.emailUrl
                  }
                } as MediaObject;
                lastMediaItemKeyForViewCaptureRef.current = fetchEmailId;
                setViewCapture(!!newMediaItem.mediaContext?.capture);
                newMediaItem.srcToUse = mboxData.emailUrl;
                lastMediaItemKeyRef.current = getMediaDisplayIdentityKey(newMediaItem);
                setItemToUse(newMediaItem);
              }
            } else {
              // Only update state if this fetch is still for the current email
              if (currentMboxFetchRef.current?.emailId === fetchEmailId) {
                lastMediaItemKeyForViewCaptureRef.current = fetchEmailId;
                setViewCapture(!!mediaItem.mediaContext?.capture);
                const fallbackItem = {
                  ...mediaItem,
                  srcToUse: mediaItem?.mediaContext?.previewUrl || mediaItem?.mediaSrc || mediaItem?.mediaUrl || undefined
                };
                lastMediaItemKeyRef.current = getMediaDisplayIdentityKey(fallbackItem);
                setItemToUse(fallbackItem);
                // Reset the fetched email ID if fetch failed
                mboxFetchedEmailIdRef.current = null;
              }
            }
          } catch (error) {
            // If aborted, don't update state
            if (error instanceof Error && error.name === 'AbortError') {
              return;
            }
            // Only update state if this fetch is still for the current email
            if (currentMboxFetchRef.current?.emailId === fetchEmailId) {
              lastMediaItemKeyForViewCaptureRef.current = fetchEmailId;
              setViewCapture(!!mediaItem.mediaContext?.capture);
              const fallbackItem = {
                ...mediaItem,
                srcToUse: mediaItem?.mediaContext?.previewUrl || mediaItem?.mediaSrc || mediaItem?.mediaUrl || undefined
              };
              lastMediaItemKeyRef.current = getMediaDisplayIdentityKey(fallbackItem);
              setItemToUse(fallbackItem);
              // Reset the fetched email ID if fetch failed
              mboxFetchedEmailIdRef.current = null;
            }
          }
        };
        fetchMbox();
      }
    } else {
      // Cancel any pending mbox fetch when switching to non-mbox source
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
      // Reset the fetched email ID when not dealing with mbox
      if (mboxFetchedEmailIdRef.current !== null) {
        mboxFetchedEmailIdRef.current = null;
      }
      const checkSourceInfo = async () => {
        // use the fullscreen media item if in fullScreen, otherwise use the media item
        let currentMediaItem = isLinkedMedia && linkedMediaItem ? linkedMediaItem : isFullscreen ? fullscreenMediaItem : mediaItem;
        if (!isFullscreen && fullscreenMediaItem?.mediaContext?.pageNumber && currentMediaItem) {
          const fullScreenKey = getMediaDisplayIdentityKey(fullscreenMediaItem);
          if (fullScreenKey === lastMediaItemKeyRef.current) {
            currentMediaItem = {
              ...currentMediaItem,
              mediaContext: {
                ...currentMediaItem.mediaContext,
                pageNumber: fullscreenMediaItem.mediaContext.pageNumber
              }
            };
          }
        }
        if (!!currentMediaItem) {
          const mediaItemKey = getMediaDisplayIdentityKey(currentMediaItem);
          const mediaItemChanged = mediaItemKey !== lastMediaItemKeyForViewCaptureRef.current;
          const initializeViewCapture = (value: boolean) => {
            if (mediaItemChanged) {
              lastMediaItemKeyForViewCaptureRef.current = mediaItemKey;
              setViewCapture(value);
            }
          };
          try {
            const newMediaItem = {
              ...currentMediaItem
            };
            if ((!newMediaItem?.mediaSrc || newMediaItem?.mediaSrc === '') && (!newMediaItem?.mediaUrl || newMediaItem?.mediaUrl === '')) {
              if ((newMediaItem as any)?.previewFilePath && (newMediaItem as any)?.previewFilePath !== '') {
                newMediaItem.mediaSrc = (newMediaItem as any)?.previewFilePath;
              } else if ((newMediaItem as any)?.sourceFilePath && (newMediaItem as any)?.sourceFilePath !== '') {
                newMediaItem.mediaSrc = (newMediaItem as any)?.sourceFilePath;
              } else {
                newMediaItem.mediaSrc = (newMediaItem as any)?.sourceUrl || (newMediaItem as any)?.originalUrl;
              }
            }
            if (!(isLinkedMedia || linkedMediaItem) && (newMediaItem?.mediaSrc || newMediaItem?.mediaUrl)) {
              const sourceInfo = await getCurrentSourceInfo(newMediaItem?.mediaSrc || newMediaItem?.mediaUrl || '');
              if (sourceInfo) {
                newMediaItem.sourceInfo = sourceInfo;
                const srcToUseIn = getSrcToUse(newMediaItem);
                let newMediaType = getMediaType(newMediaItem);
                if (newMediaType === 'unknown' && sourceInfo?.previewObjectUrl) {
                  if (sourceInfo.previewContentType) {
                    const previewMediaType = checkSourceContentType({
                      ...sourceInfo,
                      sourceContentType: sourceInfo.previewContentType
                    });
                    if (previewMediaType && previewMediaType !== 'unknown') {
                      newMediaType = previewMediaType;
                    }
                  }
                  if (newMediaType === 'unknown' && sourceInfo.previewFileType) {
                    const ft = sourceInfo.previewFileType.toLowerCase();
                    const mappedType = FileTypes[ft as keyof typeof FileTypes] as MediaType | undefined;
                    if (mappedType && mappedType !== 'unknown') {
                      newMediaType = mappedType;
                    }
                  }
                }
                if (newMediaType !== 'unknown') {
                  newMediaItem.mediaType = newMediaType;
                }
                initializeViewCapture(!!newMediaItem.mediaContext?.capture);
                newMediaItem.srcToUse = srcToUseIn;
                lastMediaItemKeyRef.current = getMediaDisplayIdentityKey(newMediaItem);
                setItemToUse(newMediaItem);
                if (getMediaType(newMediaItem) === 'pdf' || getMediaType(newMediaItem) === 'docx') {
                  setPdfPageReady(true);
                }
                return;
              }
            }
          } catch (error) {
            // just use the original media item
          }
          initializeViewCapture(!!currentMediaItem.mediaContext?.capture);
          const fallbackItem = {
            ...currentMediaItem,
            srcToUse: getSrcToUse(currentMediaItem)
          };
          lastMediaItemKeyRef.current = getMediaDisplayIdentityKey(fallbackItem);
          setItemToUse(fallbackItem);
          if (getMediaType(fallbackItem) === 'pdf' || getMediaType(fallbackItem) === 'docx') {
            setPdfPageReady(true);
          }
        }
      };
      checkSourceInfo();
    }

    // Cleanup function to cancel any pending fetch when component unmounts or effect re-runs
    return () => {
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
    };
  }, [mediaItem, isFullscreen, fullscreenMediaItem, linkedMediaItem, isTable, isAccordion, isUnifiedSources, captureFeatureEnabled]);
  useEffect(() => {
    if (!itemToUse) return;
    let {
      width,
      height
    } = normalContainerDimensions || {
      width: mediaDisplayRef.current?.parentElement?.clientWidth,
      height: mediaDisplayRef.current?.parentElement?.clientHeight
    };
    if (isTable || isAccordion || isUnifiedSources) {
      width = 116;
      height = itemToUse?.mediaType === 'image' ? 126 : 90;
    }
    if (windowWidth && windowWidth < 450) {
      width = windowWidth * 0.8;
      if (windowHeight) {
        height = windowHeight * 0.5;
      } else {
        height = windowWidth;
      }
    } else if (containerWidth && containerHeight) {
      width = containerWidth;
      height = containerHeight;
    }

    // Adjust dimensions when sources are expanded
    if (sourcesExpanded && (isPresentation || isSourceCard) && !isFullscreen && height) {
      height = height * 0.8; // Reduce height by 20% when sources are expanded
    }
    setNormalContainerWidth(width);
    setNormalContainerHeight(height);
  }, [itemToUse, isFullscreen, fullscreenMediaItem, linkedMediaItem, isTable, isAccordion, isUnifiedSources, featureFlags]);
  const renderMediaDisplay = () => {
    const displayItem = itemToUse && lastMediaItemKeyRef.current === getMediaDisplayIdentityKey(itemToUse) ? itemToUse : undefined;
    // const displayItem = displayItemRaw ? mediaObjectForMetaPreviewMismatch(displayItemRaw) : undefined;
    const isCapture = captureFeatureEnabled && viewCapture && displayItem?.mediaContext?.capture;
    const isRotatable = !isTitleSlide && (rotatableMediaTypes.includes(displayItem?.mediaType || '') || isCapture);
    const isZoomable = isFullscreen && (zoomableMediaTypes.includes(displayItem?.mediaType || '') || isCapture);
    const showExpandInToolbar = isLinkedMedia || isFullscreen || showToolbarExpandButton;
    const showToolbar = !isTable && !isAccordion && !isUnifiedSources && !isPreview && (isRotatable || isZoomable || showExpandInToolbar);
    return <div ref={mediaDisplayRef} className={`mediaDisplay relative w-full top-0 z-10
          ${isFullscreen ? 'flex flex-col overflow-hidden h-full' : `${isMediaShareDrawer ? 'overflow-visible' : 'overflow-hidden'} ${normalContainerHeight ? `h-[calc(100%-10px)] min-h-[${normalContainerHeight - 10}px]` : 'h-full'}`}
        `} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onMouseMove={onMouseMove} data-sentry-component="renderMediaDisplay" data-sentry-source-file="mediaDisplay.tsx">
        {showToolbar && <div className={`mediaDisplayControls box-border ${isFullscreen ? 'relative p-1 top-0 left-0 w-full z-[10000] flex-shrink-0' : ''} flex flex-row bg-gray-700 pt-1 pb-2 rounded-t-md px-1 items-center justify-between`} onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}>
            <div className='relative z-[10000] flex flex-row items-center justify-start gap-1'>
              {isRotatable && <>
                  <div className='z-[10000] flex items-center justify-center rounded-md hover:bg-white/10 w-[24px] h-[24px] cursor-pointer' onClick={() => handleSetRotation('c')}>
                    <ArrowClockwiseIcon size={20} />
                  </div>
                  <div className='z-[10000] flex items-center justify-center rounded-md hover:bg-white/10 w-[24px] h-[24px] cursor-pointer' onClick={() => handleSetRotation('cc')}>
                    <ArrowCounterClockwiseIcon size={20} />
                  </div>
                </>}
              {isZoomable && <>
                  <div className='z-[10000] flex items-center justify-center rounded-md hover:bg-white/10 w-[24px] h-[24px] cursor-pointer' onClick={zoomIn}>
                    <MagnifyingGlassPlusIcon size={20} />
                  </div>
                  <div className='z-[10000] flex items-center justify-center rounded-md hover:bg-white/10 w-[24px] h-[24px] cursor-pointer' onClick={zoomOut}>
                    <MagnifyingGlassMinusIcon size={20} />
                  </div>
                </>}
              {captureFeatureEnabled && <div className='flex flex-row items-center justify-center gap-0'>
                  {displayItem?.mediaContext?.capture && <Tooltip onClick={event => {
              event.preventDefault();
            }} className='text-white bg-tooltip-bg rounded-sm p-2' content={viewCapture ? 'View the original media item' : 'View the captured image preview for this item'} placement='bottom' showArrow={true} shouldFlip={true} closeDelay={100} classNames={CAPTURE_TOOLTIP_CLASSNAMES}>
                      <Button isDisabled={!displayItem?.mediaContext?.capture || displayItem?.mediaContext?.capture === ''} className='block text-center text-xs box-border w-[36px] h-[36px] max-w-[36px] bg-gray-700 hover:bg-white/10 rounded-full p-0' onPress={() => {
                setViewCapture(!viewCapture);
              }}>
                        <div className={`relative top-0 left-0 ${viewCapture ? 'text-white/70' : 'text-white/10'}`}>CAPTURE</div>
                        <div className={`relative top-0 left-0 ${viewCapture ? 'text-white/10' : 'text-white/70'}`}>ORIGINAL</div>
                      </Button>
                    </Tooltip>}
                  {(!isPresentation || isEventModal || isSourceCard) && <Tooltip onClick={event => {
              event.preventDefault();
            }} className='text-white bg-tooltip-bg rounded-sm p-2' content={captureMode ? "Click to cancel capture mode" : "Capture a section of the media item for display (click to enter capture mode, then click and drag on the media to select a section)"} placement='bottom' showArrow={true} shouldFlip={true} closeDelay={100} classNames={CAPTURE_TOOLTIP_CLASSNAMES}>
                    <Button isIconOnly={true} className='box-border w-[36px] h-[36px] max-w-[36px] bg-gray-700 hover:bg-white/10 rounded-full p-0' onPress={() => {
                if (captureMode) {
                  setCaptureRegion(undefined);
                }
                setCaptureMode(prev => !prev);
              }}>
                      <CropIcon size={20} className={captureMode ? 'text-green-500' : 'text-white/70'} />
                    </Button>
                  </Tooltip>}
                  {!!updateMediaContext && <Tooltip onClick={event => {
              event.preventDefault();
            }} className='text-white bg-tooltip-bg rounded-sm p-2' content={!!capturedDataAvailable?.captureData && capturedDataAvailable?.captureData !== '' ? 'Add/Update the captured image preview for this item' : 'No captured image available'} placement='bottom' showArrow={true} shouldFlip={true} closeDelay={100} classNames={CAPTURE_TOOLTIP_CLASSNAMES}>
                      <Button isDisabled={!capturedDataAvailable?.captureData || capturedDataAvailable?.captureData === ''} isIconOnly={true} className='add-capture-button box-border w-[36px] h-[36px] max-w-[36px] bg-gray-700 hover:bg-white/10 rounded-full p-0' onPress={() => {
                if (!capturedDataAvailable?.captureData || capturedDataAvailable?.captureData === '') return;
                setApplyingCapturedData(true);
                updateMediaContext('capture', capturedDataAvailable?.captureData || undefined);
                setTimeout(() => {
                  setApplyingCapturedData(false);
                  setViewCapture(true);
                }, 1000);
              }}>
                        <FrameCornersIcon size={20} className={applyingCapturedData ? 'text-green-500' : 'text-white/70'} />
                        <span className='text-white/70 -ml-[8px] -mt-[8px]'>+</span>
                      </Button>
                    </Tooltip>}
                  {!!updateMediaContext && <Tooltip onClick={event => {
              event.preventDefault();
            }} className='text-white bg-tooltip-bg rounded-sm p-2' content={'Remove the captured image preview for this item'} placement='bottom' showArrow={true} shouldFlip={true} closeDelay={100} classNames={CAPTURE_TOOLTIP_CLASSNAMES}>
                      <Button isDisabled={!displayItem?.mediaContext?.capture || displayItem?.mediaContext?.capture === ''} isIconOnly={true} className='box-border w-[36px] h-[36px] max-w-[36px] bg-gray-700 hover:bg-white/10 rounded-full p-0' onPress={() => {
                if (!displayItem?.mediaContext?.capture || displayItem?.mediaContext?.capture === '') return;
                setRemovingCapturedData(true);
                setViewCapture(false);
                updateMediaContext('capture', undefined);
                setTimeout(() => {
                  setRemovingCapturedData(false);
                  setViewCapture(false);
                }, 1000);
              }}>
                        <FrameCornersIcon size={20} className={removingCapturedData ? 'text-green-500' : 'text-white/70'} />
                        <span className='text-white/70 -ml-[8px] -mt-[8px]'>-</span>
                      </Button>
                    </Tooltip>}
                </div>}
            </div>
            {isLinkedMedia ? <div className='relative z-[10000] flex items-center justify-center
                  rounded-md hover:bg-white/10 w-[24px] h-[24px] cursor-pointer' onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setLinkedMediaItem(null);
        }}>
                <XCircleIcon size={20} />
              </div> : isFullscreen || showToolbarExpandButton ? <div className='relative z-[10000] flex items-center justify-center
                  rounded-md hover:bg-white/10 w-[24px] h-[24px] cursor-pointer' id='toggle-fullscreen-button' onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          toggleFullScreen();
        }}>
                {isFullscreen ? <ArrowsInSimpleIcon size={20} data-testid='minimize-button' /> : <ArrowsOutSimpleIcon size={20} data-testid='maximize-button' />}
              </div> : null}
          </div>}
        {displayItem?.mediaType === 'image' && displayItem?.srcToUse || displayItem?.mediaContext?.capture && viewCapture ? <div className={`mediaDisplayImageContainer flex items-start justify-left ${isFullscreen ? 'flex-1 min-h-0' : isMediaShareDrawer ? 'min-h-0' : 'h-[calc(100%-30px)]'} ${localContainerWidth || 'w-full'}`}>
            {!viewCapture && getSrcToUse(displayItem) && imageErrors[getSrcToUse(displayItem) || ''] ? <div className='mediaDisplayImageErrors flex flex-col h-full items-center justify-center w-full break-all wrap'>
                <ImageNotFound id={1} src={featureFlags?.['powerUserOrg'] ? `${getSrcToUse(displayItem)}` : `${extractPathAfterBulk(getSrcToUse(displayItem) || '', true).truncatedFilePath}`} isThumbnail={isAccordion || isTable || isUnifiedSources} />
              </div> : <div className={`
                  mediaDisplayImageDiv
                  flex flex-row items-start justify-center w-full h-full
                  ${isFullscreen ? `overflow-scroll` : isMediaShareDrawer ? 'overflow-visible' : 'overflow-hidden'}
                `} style={{
          ...(isFullscreen && (!temporaryRotation && !displayItem.mediaContext?.rotation || !updateMediaContext && (temporaryRotation === 0 || temporaryRotation === 180) || temporaryRotation === undefined && (displayItem.mediaContext?.rotation === 0 || displayItem.mediaContext?.rotation === 180)) ? {
            paddingTop: zoomLevel > 0 ? `${imageDimensions?.height ? imageDimensions?.height / 100 * zoomLevel : 0}px` : ''
          } : {}),
          ...(isFullscreen && (!updateMediaContext && (temporaryRotation === 90 || temporaryRotation === 270) || temporaryRotation === undefined && (displayItem.mediaContext?.rotation === 90 || displayItem.mediaContext?.rotation === 270)) ? {
            paddingTop: zoomLevel > 0 ? `${imageDimensions?.width ? imageDimensions?.width / 100 * zoomLevel + (imageDimensions?.width - imageDimensions?.height) / 2 : 0}px` : ''
          } : {})
        }}>
                <img aria-label="Image" src={viewCapture ? displayItem?.mediaContext?.capture || '' : `/api/proxy/${encodeURIComponent(displayItem?.srcToUse || '')}`} alt={displayItem?.srcToUse && !viewCapture ? extractPathAfterBulk(displayItem?.srcToUse, true).truncatedFilePath || 'Media image' : 'Media image'} className={`mediaDisplayImageContent rounded-sm select-none
                    ${isEventModal && !isFullscreen ? 'h-[450px] w-[450px] min-w-[450px] min-h-[450px] max-w-[450px] max-h-[450px]' : ''}
                    ${(isPresentation || isSourceCard) && !isFullscreen ? `max-w-full max-h-full` : ''}
                    ${isMediaShareDrawer && !isFullscreen ? 'max-w-full w-full h-auto object-contain' : ''}
                    ${isAccordion ? `w-[138px] max-h-[178px] overflow-hidden` : ''}
                    ${isTable ? `w-[120px] h-[90px] overflow-hidden` : ''}
                    ${isUnifiedSources ? 'h-[128px]' : ''}
                    ${isFullscreen ? 'object-contain' : 'object-contain'}
                    ${!isFullscreen && !updateMediaContext && temporaryRotation === 0 ? `transform rotate-0` : ''}
                    ${!isFullscreen && !updateMediaContext && temporaryRotation === 90 ? `transform rotate-90` : ''}
                    ${!isFullscreen && !updateMediaContext && temporaryRotation === 180 ? `transform rotate-180` : ''}
                    ${!isFullscreen && !updateMediaContext && temporaryRotation === 270 ? `transform -rotate-90` : ''}
                    ${!isFullscreen && displayItem.mediaContext?.rotation === 0 && temporaryRotation === undefined ? `transform rotate-0` : ''}
                    ${!isFullscreen && displayItem.mediaContext?.rotation === 90 && temporaryRotation === undefined ? `transform rotate-90` : ''}
                    ${!isFullscreen && displayItem.mediaContext?.rotation === 180 && temporaryRotation === undefined ? `transform rotate-180` : ''}
                    ${!isFullscreen && displayItem.mediaContext?.rotation === 270 && temporaryRotation === undefined ? `transform -rotate-90` : ''}
                  `} style={{
            ...(isFullscreen && (!temporaryRotation && !displayItem.mediaContext?.rotation || !updateMediaContext && temporaryRotation === 0 || temporaryRotation === undefined && displayItem.mediaContext?.rotation === 0) ? {
              transform: `scale(${1 + zoomLevel * 0.02}) rotate(0deg)`,
              marginTop: zoomLevel < 0 ? `${imageDimensions?.height ? imageDimensions?.height / 100 * zoomLevel : 0}px` : ''
            } : {}),
            ...(isFullscreen && (!updateMediaContext && temporaryRotation === 180 || temporaryRotation === undefined && displayItem.mediaContext?.rotation === 180) ? {
              transform: `scale(${1 + zoomLevel * 0.02}) rotate(180deg)`,
              marginTop: zoomLevel < 0 ? `${imageDimensions?.height ? imageDimensions?.height / 100 * zoomLevel : 0}px` : ''
            } : {}),
            ...(isFullscreen && (!updateMediaContext && temporaryRotation === 90 || temporaryRotation === undefined && displayItem.mediaContext?.rotation === 90) ? {
              transform: `scale(${1 + zoomLevel * 0.02}) rotate(90deg)`,
              marginTop: zoomLevel <= 0 ? `${imageDimensions?.width ? imageDimensions?.width / 100 * zoomLevel + (imageDimensions?.width - imageDimensions?.height) / 2 : 0}px` : ''
            } : {}),
            ...(isFullscreen && (!updateMediaContext && temporaryRotation === 270 || temporaryRotation === undefined && displayItem.mediaContext?.rotation === 270) ? {
              transform: `scale(${1 + zoomLevel * 0.02}) rotate(270deg)`,
              marginTop: zoomLevel <= 0 ? `${imageDimensions?.width ? imageDimensions?.width / 100 * zoomLevel + (imageDimensions?.width - imageDimensions?.height) / 2 : 0}px` : ''
            } : {})
          }} onLoad={(imageData: any) => {
            onImageLoaded(imageData);
          }} onError={e => {
            if (!viewCapture && displayItem?.srcToUse) {
              handleImageErrors({
                [displayItem?.srcToUse]: {
                  fileNotFound: false
                }
              }, displayItem);
            }
          }} />
              </div>}
          </div> : displayItem?.mediaType === 'pdf' && pdfPageReady && !!displayItem?.srcToUse && displayItem?.srcToUse.trim() !== "" ? <div className={`w-full ${isFullscreen ? 'flex-1 min-h-0' : 'h-full'}`}>
            <PdfViewer media={displayItem} updateMediaContext={!!handleUpdateMediaContext && !isPreview ? handleUpdateMediaContext : undefined} isAccordion={isAccordion} isTable={isTable} isFullscreen={isFullscreen} isPresentation={isPresentation || isSourceCard} isTitleSlide={isTitleSlide} isUnifiedSources={isUnifiedSources} temporaryRotation={temporaryRotation} zoomLevel={zoomLevel} containerWidth={containerWidth} containerHeight={containerHeight} onPageNumberChange={handlePdfPageChange} />
          </div> : displayItem?.mediaType === 'docx' && pdfPageReady && !!displayItem?.srcToUse && displayItem?.srcToUse.trim() !== "" ? <div className={`w-full ${isFullscreen ? 'flex-1 min-h-0' : 'h-full'}`}>
            <PdfViewer media={displayItem} updateMediaContext={!!handleUpdateMediaContext && !isPreview ? handleUpdateMediaContext : undefined} isAccordion={isAccordion} isTable={isTable} isFullscreen={isFullscreen} isPresentation={isPresentation || isSourceCard} isTitleSlide={isTitleSlide} isUnifiedSources={isUnifiedSources} temporaryRotation={temporaryRotation} zoomLevel={zoomLevel} containerWidth={containerWidth} containerHeight={containerHeight} apiPath="ms-word" onPageNumberChange={handlePdfPageChange} />
          </div> : displayItem?.mediaType === 'spreadsheet' && /\.(xlsx?|csv|tsv)$/i.test(displayItem?.srcToUse ?? '') ? <div data-testid='xlsx-viewer-container' className={`flex flex-row w-full rounded-md z-[10000] overflow-hidden
              ${isEventModal && !isFullscreen ? 'bg-gray-900 h-[450px] max-w-[450px] max-h-[450px] items-center justify-center' : ''}
              ${(isPresentation || isSourceCard) && !isFullscreen ? 'items-stretch min-h-0' : ''}
              ${!isPresentation && !isSourceCard && !isFullscreen && !isAccordion && !isTable ? 'items-center justify-center' : ''}
              ${isFullscreen ? 'flex-1 min-h-0 w-full items-stretch' : ''}
              ${isAccordion ? 'overflow-hidden items-center justify-center' : ''}
              ${isTable ? 'items-center justify-center' : ''}
          `} style={(isPresentation || isSourceCard) && !isFullscreen && normalContainerHeight ? {
        height: `${normalContainerHeight - (showToolbar ? 40 : 10)}px`
      } : undefined}>
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen ? <div className="flex items-center justify-center w-full h-full">
                <TableIcon size={25} weight="light" />
              </div> : <XlsxViewer s3Url={displayItem?.mediaUrl || displayItem?.mediaSrc || displayItem?.srcToUse || ''} />}
          </div> : displayItem?.srcToUse && isMetaFile(displayItem) ? <div className={`flex w-full min-w-0 max-w-full flex-row items-center justify-center overflow-x-hidden ${isFullscreen ? 'min-h-0 flex-1 overflow-y-auto custom-scrollbar2' : 'h-full'}`}>
            <MetaFileDisplay src={displayItem?.srcToUse || ''} mediaItem={displayItem} caseId={metaViewerCaseId} isFullscreen={isFullscreen} isUnifiedSources={isUnifiedSources} isAccordion={isAccordion} isTable={isTable} previewWidthPx={!isFullscreen && normalContainerWidth ? normalContainerWidth - 70 : undefined} previewHeightPx={!isFullscreen && normalContainerHeight ? normalContainerHeight - (showToolbar ? 90 : 50) : undefined} hideSocialViewerButton={hideMetaSocialViewerButton} />
          </div> : (displayItem?.mediaType === 'markdown' || displayItem?.mediaType === 'text') && displayItem?.srcToUse ? <div className={`flex flex-row items-center justify-center w-full ${isFullscreen ? 'flex-1 min-h-0 overflow-y-auto custom-scrollbar2' : 'h-full'}`}>
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen ? <div className="flex flex-row items-center justify-center h-[90px]">
                <FileTextIcon className="text-white/65" size={25} />
              </div> : <div className={`box-border text-black bg-gray-600 rounded-sm
                  ${(isPresentation || isSourceCard) && !isFullscreen && normalContainerWidth ? `sm:max-w-[${normalContainerWidth - 70}px] w-[80vw]` : `${!isFullscreen ? 'sm:max-w-[400px]' : ''}`}
                  ${isFullscreen ? 'sm:w-[80vw] max-h-[80vh]' : `h-[${normalContainerHeight ? normalContainerHeight - (showToolbar ? 90 : 50) : '400'}px]`}
                `} style={{
          height: isFullscreen ? '100%' : `${normalContainerHeight ? normalContainerHeight - (showToolbar ? 90 : 50) : '400'}px`,
          width: isFullscreen ? '100%' : `${normalContainerWidth ? normalContainerWidth - 70 : '400'}px`,
          maxHeight: isFullscreen ? '100%' : `${normalContainerHeight ? normalContainerHeight - (showToolbar ? 90 : 50) : '400'}px`,
          maxWidth: isFullscreen ? '100%' : `${normalContainerWidth ? normalContainerWidth - 70 : '400'}px`
        }}>
                {displayItem?.mediaType === 'markdown' || isMarkdownPreview(displayItem) ? <MarkdownViewer s3Url={displayItem?.srcToUse || displayItem?.mediaUrl || displayItem?.mediaSrc || ''} /> : isPlainTextFile(displayItem) ? <PlainTextViewer s3Url={displayItem?.mediaUrl || displayItem?.mediaSrc || displayItem?.srcToUse || ''} /> : viewAsCsvLike ? <XlsxViewer s3Url={displayItem?.mediaUrl || displayItem?.mediaSrc || displayItem?.srcToUse || ''} /> : <div ref={textRef} className="box-border overflow-y-auto overflow-x-auto custom-scrollbar2 text-black break-all w-full h-full p-2 bg-white">
                  </div>}
              </div>}
          </div> : displayItem?.mediaType === 'website' && displayItem?.srcToUse ? <iframe src={displayItem?.srcToUse} className={`${localContainerWidth || 'w-full'} ${isFullscreen ? 'flex-1 min-h-0' : 'h-64'} mt-4 border-0`} title={displayItem?.srcToUse} /> : (displayItem?.mediaType === 'video' || displayItem?.mediaType === 'audio') && displayItem?.srcToUse ? <>
            {(isUnifiedSources || isAccordion || isTable) && displayItem?.mediaType === 'audio' ? <div className={`box-border m-1 flex flex-col items-center justify-center text-center ${localContainerWidth || 'w-full'} h-[80px] rounded-sm bg-gray-900`}>
                <div style={{
            overflow: 'hidden',
            width: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '1.5em'
          }}>
                  <div style={{
              display: 'inline-block',
              whiteSpace: 'nowrap',
              width: 'max-content'
            }} className="text-white/65 text-[11px]">
                    {(() => {
                const url = (displayItem.mediaSrc || displayItem.mediaUrl || '').replace(/@/g, '');
                const filename = url.split('/').pop() || '';
                const maxLength = 8;
                const startIndex = Math.max(0, filename.length - maxLength);
                const truncatedName = filename.length > maxLength ? `...${filename.slice(startIndex, startIndex + maxLength)}` : filename;
                const startTime = displayItem.mediaContext?.startTime;
                const startTimeInfo = startTime && startTime !== "0" && startTime !== "0.0" ? ` (Start: ${formatTimeIndex(startTime)})` : '';
                return <>
                          <span>{truncatedName}{startTimeInfo}</span>
                        </>;
              })()}
                  </div>
                </div>
                <FileAudioIcon size={24} className="text-white/65 mt-1" />
              </div> : <div data-testid="media-player-container" id={isFullscreen ? 'media-player-container-fullscreen' : 'media-player-container'} className={`box-border 
                  ${isPresentation || isSourceCard ? '' : isFullscreen ? 'top-0' : ''}
                  ${isFullscreen ? 'flex-1 min-h-0 w-full sm:w-full' : ''}
                  ${isTable ? `p-[3px] h-[90px] ${!isGoingToRenderSetStillFramePrompt ? 'w-[120px]' : ''}` : ''}
                  ${isUnifiedSources ? `h-[90px] ${!isGoingToRenderSetStillFramePrompt ? 'w-[120px]' : ''}` : ''}
                  ${isGoingToRenderSetStillFramePrompt ? 'h-[90px] w-[100px]' : ''}
                `}>
                <MediaPlayer mediaItem={displayItem} transcriptSrc={isAccordion || isUnifiedSources ? undefined : (displayItem?.transcriptUrl ? `THE_TRANSCRIPT${displayItem.transcriptUrl}` : null) || (displayItem?.sourceInfo?.transcriptObjectUrl ? `THE_TRANSCRIPT${displayItem.sourceInfo.transcriptObjectUrl}` : null) || `TRANSCRIPT${displayItem?.originalUrl || displayItem?.mediaSrc || displayItem?.mediaUrl}` || undefined} disableHotkeys={isAccordion || isPresentation || isSourceCard || isUnifiedSources} showTranscript={isAccordion ? false : displayItem.mediaContext?.showTranscript !== undefined ? displayItem.mediaContext?.showTranscript : showTranscript} showTranscriptMarkers={displayItem.mediaContext?.showTranscriptMarkers || false} updateMediaContext={handleUpdateMediaContext} transcriptPosition={isEventModal ? 'below' : isFullscreen ? 'side' : displayItem.mediaContext?.transcriptPosition || transcriptPosition} useStillFrame={isAccordion || isTable} isAccordion={isAccordion} isTable={isTable} isEditing={isEventModal} isUnifiedSources={isUnifiedSources} isFullscreen={isFullscreen} isPresentation={isPresentation || isSourceCard} temporaryRotation={temporaryRotation} sourcesExpanded={sourcesExpanded} />
              </div>}
          </> : displayItem?.mediaType === 'html' && displayItem?.srcToUse ? <>
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen ? <div className="flex items-center justify-center h-full bg-gray-600 rounded-md">
                <FileHtmlIcon size={25} />
              </div> : <iframe className={`border-1 mr-[20px] bg-white w-full
                  ${isFullscreen ? 'h-[85vh]' : `max-h-full h-[440px]`}
                `} src={`/api/proxy/PROCESSHTML${encodeURIComponent(displayItem?.srcToUse || '')}?chunk=${displayItem.mediaContext?.htmlChunk}`} title={displayItem?.srcToUse} allow='autoplay; clipboard-write; encrypted-media; picture-in-picture' allowFullScreen />}
          </> : displayItem?.mediaType === 'youtube' && displayItem?.srcToUse ? <iframe className={`aspect-video ${localContainerWidth || 'w-full'} ${isFullscreen ? 'h-[85vh]' : 'h-full'} border-0`} src={`https://www.youtube.com/embed/${(displayItem?.srcToUse || '').split('embed/').pop()}`} title={displayItem?.srcToUse} allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture' allowFullScreen /> : null}
        <Modal isOpen={!captureRegionOverlay && captureRegion && captureRegion.width !== undefined && captureRegion.width > 0 && captureRegion.height !== undefined && captureRegion.height > 0 && captureRegion.x !== undefined && captureRegion.y !== undefined} onOpenChange={() => setCaptureRegion(undefined)} classNames={CAPTURE_MODAL_CLASSNAMES} portalContainer={document.body} data-sentry-element="Modal" data-sentry-source-file="mediaDisplay.tsx">
          <ModalContent className='captureModalContent2 my-0 mx-0 m-0 sm:my-0 sm:mx-0 sm:m-0' style={{
          minWidth: `${(captureRegion?.width || 500) + 40}px`,
          minHeight: `${(captureRegion?.height || 500) + 154}px`,
          width: `${(captureRegion?.width || 500) + 40}px`,
          height: `${(captureRegion?.height || 500) + 154}px`
        }} data-sentry-element="ModalContent" data-sentry-source-file="mediaDisplay.tsx">
            <ModalHeader className='captureModalHeader2 text-2xl font-bold bg-gray-700 text-white' data-sentry-element="ModalHeader" data-sentry-source-file="mediaDisplay.tsx">
              Capture Region
            </ModalHeader>
            <ModalBody style={{
            width: `${(captureRegion?.width || 500) + 10}px`,
            height: `${(captureRegion?.height || 500) + 10}px`
          }} data-sentry-element="ModalBody" data-sentry-source-file="mediaDisplay.tsx">
              <div ref={capModalRef} className={`captureModalContent3 flex items-center justify-center max-w-[70vw] max-h-[70vh] px-0 object-contain overflow-y-auto overflow-x-auto custom-scrollbar2`} style={{
              objectFit: 'contain',
              width: `${(captureRegion?.width || 500) + 10}px`,
              height: `${(captureRegion?.height || 500) + 10}px`
            }} />
            </ModalBody>
            <ModalFooter className='captureModalFooter2 flex flex-row items-center justify-end gap-1' data-sentry-element="ModalFooter" data-sentry-source-file="mediaDisplay.tsx">
              <Button onPress={() => {
              setCaptureMode(false);
              setCaptureRegion(undefined);
            }} color='danger' data-sentry-element="Button" data-sentry-source-file="mediaDisplay.tsx">Cancel</Button>
              <Button onPress={() => {
              handleCaptureRegion(captureRegion);
              setCaptureMode(false);
            }} color='primary' data-sentry-element="Button" data-sentry-source-file="mediaDisplay.tsx">Save</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {captureRegionOverlay && <div style={{
        boxSizing: 'border-box',
        position: 'absolute',
        pointerEvents: 'none',
        border: '3px dashed red',
        zIndex: 100000000,
        top: `${captureRegionOverlay.y}px`,
        left: `${captureRegionOverlay.x}px`,
        width: `${captureRegionOverlay.width}px`,
        height: `${captureRegionOverlay.height}px`,
        minWidth: `${captureRegionOverlay.width}px`,
        minHeight: `${captureRegionOverlay.height}px`,
        maxWidth: `${captureRegionOverlay.width}px`,
        maxHeight: `${captureRegionOverlay.height}px`
      }}>
          </div>}
      </div>;
  };
  if ((isFullscreen || isLinkedMedia && linkedMediaItem) && !isTable && !isAccordion && !isUnifiedSources) {
    const fullscreenOverlay = <div className="fixed inset-0 w-[100vw] h-[100vh] z-[10000]">
        <div id="fullScreenMediaDisplayOuter" className="fullScreenMediaDisplayOuter box-border z-[10000] left-0 top-0 w-full h-full bg-black/20">
          <div id="fullScreenMediaDisplayInner" className="box-border relative z-[10000] bg-black h-full w-full max-h-full max-w-full
            items-center flex flex-row justify-center">
            {renderMediaDisplay()}
          </div>
        </div>
      </div>;
    return typeof document !== 'undefined' ? createPortal(fullscreenOverlay, document.body) : fullscreenOverlay;
  } else {
    return renderMediaDisplay();
  }
});
MediaDisplayComponent.displayName = 'MediaDisplay';
export const MediaDisplay = memo(MediaDisplayComponent);
MediaDisplay.displayName = 'MediaDisplay';
export default MediaDisplay;